/**
 * Daily Timeline
 * Scrollable list of daily performance with expandable details
 * Shows picks or parlays based on parent's analytics mode
 */
import { formatPercent, formatROI, formatShortDay } from '@parlay-picker/shared/formatting';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import type {
  DailyParlayPerformance,
  DailyPerformance,
  DailyPick,
  ParlayInfo,
  ParlayLegInfo,
} from '../hooks/useLabDashboard';

function formatUnits(units: number): string {
  const sign = units >= 0 ? '+' : '';
  return `${sign}${units.toFixed(2)}u`;
}

function getPerformanceColor(rate: number): string {
  if (rate >= 0.55) return 'text-theme-success';
  if (rate >= 0.5) return 'text-theme-warning';
  return 'text-theme-danger';
}

function getPerformanceBg(rate: number): string {
  if (rate >= 0.55) return 'bg-theme-success/10 border-theme-success/50';
  if (rate >= 0.5) return 'bg-theme-warning/10 border-theme-warning/50';
  return 'bg-theme-danger/10 border-theme-danger/50';
}

function getTierBadgeColor(tier: string | null): string {
  if (!tier) return 'bg-theme-surface-hover';
  if (tier.toLowerCase().includes('safe')) return 'bg-teal-500/50';
  if (tier.toLowerCase().includes('value')) return 'bg-indigo-500/50';
  if (tier.toLowerCase().includes('upside')) return 'bg-orange-500/50';
  if (tier.toLowerCase().includes('long shot')) return 'bg-red-500/50';
  return 'bg-theme-surface-hover';
}

function getResultColor(result: string): string {
  if (result === 'won') return 'bg-theme-success text-white';
  if (result === 'lost') return 'bg-theme-danger text-white';
  if (result === 'pending') return 'bg-theme-primary-fill text-theme-on-primary-fill';
  return 'bg-theme-surface-hover text-theme-text-secondary'; // push/void
}

function getResultLabel(result: string): string {
  if (result === 'won') return 'W';
  if (result === 'lost') return 'L';
  if (result === 'pending') return 'P';
  if (result === 'push') return 'PU';
  return 'V'; // void
}

// =============================================================================
// Sub-Components - Picks View
// =============================================================================

function PickRow({ pick }: { pick: DailyPick }): ReactElement {
  return (
    <div
      className={`flex items-center justify-between py-1.5 px-2 rounded text-xs ${
        pick.hit === true
          ? 'bg-theme-success/10'
          : pick.hit === false
            ? 'bg-theme-danger/10'
            : 'bg-theme-surface/50'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span
          className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold ${
            pick.hit === true
              ? 'bg-theme-success text-white'
              : pick.hit === false
                ? 'bg-theme-danger text-white'
                : 'bg-theme-surface-hover text-theme-text-secondary'
          }`}
        >
          {pick.hit === true ? 'W' : pick.hit === false ? 'L' : 'P'}
        </span>
        <span className="text-gray-200 truncate">{pick.playerName || 'Game Prop'}</span>
        <span className="text-theme-text-tertiary">
          {pick.propType} {pick.direction} {pick.line}
        </span>
      </div>
      {pick.tier && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${getTierBadgeColor(pick.tier)}`}>
          {pick.tier.replace(' Play', '')}
        </span>
      )}
    </div>
  );
}

function PickDayCard({
  day,
  isExpanded,
  onToggle,
}: {
  day: DailyPerformance;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  const settledNonPush = day.hits + day.misses;
  const hitRateColor = getPerformanceColor(day.hitRate);
  const bgColor = getPerformanceBg(day.hitRate);

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${bgColor}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="text-left">
            <div className="text-sm font-medium text-gray-200">{formatShortDay(day.date)}</div>
            <div className="text-xs text-theme-text-tertiary">{day.totalPicks} picks</div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-sm">
              <span className="text-theme-success">{day.hits}W</span>
              <span className="text-theme-text-tertiary"> - </span>
              <span className="text-theme-danger">{day.misses}L</span>
              {day.pushes > 0 && (
                <>
                  <span className="text-theme-text-tertiary"> - </span>
                  <span className="text-theme-text-secondary">{day.pushes}P</span>
                </>
              )}
            </div>
          </div>

          <div className="text-right w-16">
            <div className={`text-lg font-bold ${hitRateColor}`}>
              {settledNonPush > 0 ? formatPercent(day.hitRate) : '-'}
            </div>
          </div>

          <div className="text-right w-16">
            <div
              className={`text-sm font-medium ${day.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
            >
              {settledNonPush > 0 ? formatROI(day.roi) : '-'}
            </div>
          </div>

          <div className="w-6 text-theme-text-secondary">
            <ChevronDown
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {isExpanded && day.topPicks.length > 0 && (
        <div className="border-t border-theme-border/50 p-3 space-y-1.5">
          <div className="text-xs text-theme-text-tertiary mb-2">Top picks for this day:</div>
          {day.topPicks.map((pick, idx) => (
            <PickRow key={idx} pick={pick} />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-Components - Parlays View
// =============================================================================

function getLegHitColor(hit: boolean | null): string {
  if (hit === true) return 'bg-theme-success text-white';
  if (hit === false) return 'bg-theme-danger text-white';
  return 'bg-theme-surface-hover text-theme-text-secondary'; // pending
}

function getLegHitLabel(hit: boolean | null): string {
  if (hit === true) return 'W';
  if (hit === false) return 'L';
  return '?'; // pending
}

function LegRow({ leg }: { leg: ParlayLegInfo }): ReactElement {
  return (
    <div className="flex items-center gap-2 py-1 px-2 text-[11px]">
      <span
        className={`w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-bold ${getLegHitColor(leg.hit)}`}
      >
        {getLegHitLabel(leg.hit)}
      </span>
      <span className="text-theme-text-secondary truncate flex-1">{leg.player}</span>
      <span className="text-theme-text-tertiary">{leg.prop}</span>
      {leg.odds && <span className="text-theme-text-tertiary">{leg.odds}</span>}
    </div>
  );
}

function ParlayRow({
  parlay,
  isExpanded,
  onToggle,
}: {
  parlay: ParlayInfo;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <div
      className={`rounded text-xs overflow-hidden ${
        parlay.result === 'won'
          ? 'bg-theme-success/10'
          : parlay.result === 'lost'
            ? 'bg-theme-danger/10'
            : parlay.result === 'pending'
              ? 'bg-theme-primary/10'
              : 'bg-theme-surface/50'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${getResultColor(parlay.result)}`}
          >
            {getResultLabel(parlay.result)}
          </span>
          <span className="text-gray-200 truncate">{parlay.name || parlay.tier || 'Parlay'}</span>
          <span className="text-theme-text-tertiary">{parlay.legCount} legs</span>
        </div>
        <div className="flex items-center gap-3">
          {parlay.totalOdds && (
            <span className="text-theme-text-secondary text-[10px]">{parlay.totalOdds}</span>
          )}
          <span
            className={`font-medium ${parlay.unitsWon >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
          >
            {parlay.result !== 'pending' ? formatUnits(parlay.unitsWon) : '-'}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-theme-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {isExpanded && parlay.legs && parlay.legs.length > 0 && (
        <div className="border-t border-theme-border/30 bg-black/20">
          {parlay.legs.map((leg) => (
            <LegRow key={leg.id} leg={leg} />
          ))}
        </div>
      )}
    </div>
  );
}

function ParlayDayCard({
  day,
  isExpanded,
  onToggle,
}: {
  day: DailyParlayPerformance;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  const [expandedParlays, setExpandedParlays] = useState<Set<number>>(new Set());

  const toggleParlay = (parlayId: number) => {
    setExpandedParlays((prev) => {
      const next = new Set(prev);
      if (next.has(parlayId)) {
        next.delete(parlayId);
      } else {
        next.add(parlayId);
      }
      return next;
    });
  };

  const settled = day.wins + day.losses;
  const winRateColor = settled > 0 ? getPerformanceColor(day.winRate) : 'text-theme-text-secondary';
  const bgColor =
    settled > 0 ? getPerformanceBg(day.winRate) : 'bg-theme-surface/50 border-theme-border/50';

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${bgColor}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="text-left">
            <div className="text-sm font-medium text-gray-200">{formatShortDay(day.date)}</div>
            <div className="text-xs text-theme-text-tertiary">
              {day.totalParlays} parlay{day.totalParlays !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-sm">
              <span className="text-theme-success">{day.wins}W</span>
              <span className="text-theme-text-tertiary"> - </span>
              <span className="text-theme-danger">{day.losses}L</span>
              {day.pushes > 0 && (
                <>
                  <span className="text-theme-text-tertiary"> - </span>
                  <span className="text-theme-text-secondary">{day.pushes}P</span>
                </>
              )}
              {day.pending > 0 && (
                <>
                  <span className="text-theme-text-tertiary"> - </span>
                  <span className="text-theme-primary">{day.pending}?</span>
                </>
              )}
            </div>
          </div>

          <div className="text-right w-16">
            <div className={`text-lg font-bold ${winRateColor}`}>
              {settled > 0 ? formatPercent(day.winRate) : '-'}
            </div>
          </div>

          <div className="text-right w-20">
            <div
              className={`text-lg font-bold ${day.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
            >
              {formatUnits(day.units)}
            </div>
          </div>

          <div className="w-6 text-theme-text-secondary">
            <ChevronDown
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {isExpanded && day.parlays.length > 0 && (
        <div className="border-t border-theme-border/50 p-3 space-y-1.5">
          <div className="text-xs text-theme-text-tertiary mb-2">Parlays for this day:</div>
          {day.parlays.map((parlay) => (
            <ParlayRow
              key={parlay.id}
              parlay={parlay}
              isExpanded={expandedParlays.has(parlay.id)}
              onToggle={() => toggleParlay(parlay.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface DailyTimelineProps {
  mode: 'picks' | 'parlays';
  pickData: DailyPerformance[];
  parlayData: DailyParlayPerformance[];
  pickLoading: boolean;
  parlayLoading: boolean;
  pickError: string | null;
  parlayError: string | null;
}

export function DailyTimeline({
  mode,
  pickData,
  parlayData,
  pickLoading,
  parlayLoading,
  pickError,
  parlayError,
}: DailyTimelineProps): ReactElement {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const toggleDay = (date: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  const loading = mode === 'picks' ? pickLoading : parlayLoading;
  const error = mode === 'picks' ? pickError : parlayError;
  const data = mode === 'picks' ? pickData : parlayData;

  // Calculate overall stats (memoized — must be before early returns for rules-of-hooks)
  const { summaryText, rateText, rateColor, unitsText, unitsColor } = useMemo(() => {
    if (mode === 'picks') {
      const totalPicks = pickData.reduce((sum, d) => sum + d.totalPicks, 0);
      const totalHits = pickData.reduce((sum, d) => sum + d.hits, 0);
      const totalMisses = pickData.reduce((sum, d) => sum + d.misses, 0);
      const overallHitRate =
        totalHits + totalMisses > 0 ? totalHits / (totalHits + totalMisses) : 0;
      return {
        summaryText: `${pickData.length} days | ${totalPicks} picks`,
        rateText: `${formatPercent(overallHitRate)} hit rate`,
        rateColor: getPerformanceColor(overallHitRate),
        unitsText: '',
        unitsColor: 'text-theme-text-secondary',
      };
    }
    const totalParlays = parlayData.reduce((sum, d) => sum + d.totalParlays, 0);
    const totalWins = parlayData.reduce((sum, d) => sum + d.wins, 0);
    const totalLosses = parlayData.reduce((sum, d) => sum + d.losses, 0);
    const totalUnits = parlayData.reduce((sum, d) => sum + d.units, 0);
    const overallWinRate = totalWins + totalLosses > 0 ? totalWins / (totalWins + totalLosses) : 0;
    return {
      summaryText: `${parlayData.length} days | ${totalParlays} parlays`,
      rateText: `${formatPercent(overallWinRate)} win rate`,
      rateColor: getPerformanceColor(overallWinRate),
      unitsText: formatUnits(totalUnits),
      unitsColor: totalUnits >= 0 ? 'text-theme-success' : 'text-theme-danger',
    };
  }, [mode, pickData, parlayData]);

  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary mx-auto mb-2" />
        <p className="text-theme-text-secondary text-sm">
          Loading {mode === 'picks' ? 'pick' : 'parlay'} performance...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <p className="text-theme-danger text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with summary */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Daily Performance</h2>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-theme-text-secondary">{summaryText}</span>
          <span className={rateColor}>{rateText}</span>
          {mode === 'parlays' && unitsText && <span className={unitsColor}>{unitsText}</span>}
        </div>
      </div>

      {/* Timeline list */}
      {data.length === 0 ? (
        <div className="bg-theme-surface/50 rounded-lg p-4">
          <p className="text-theme-text-secondary text-sm">
            No {mode === 'picks' ? 'pick' : 'parlay'} data available
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {mode === 'picks'
            ? pickData.map((day) => (
                <PickDayCard
                  key={day.date}
                  day={day}
                  isExpanded={expandedDays.has(day.date)}
                  onToggle={() => toggleDay(day.date)}
                />
              ))
            : parlayData.map((day) => (
                <ParlayDayCard
                  key={day.date}
                  day={day}
                  isExpanded={expandedDays.has(day.date)}
                  onToggle={() => toggleDay(day.date)}
                />
              ))}
        </div>
      )}
    </div>
  );
}
